"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { Link } from "@/components/common/Link";
import { AspectRatio } from "@/components/ui/aspect-ratio";
const useWvcLanguage = () => {
  const [lang, setLang] = React.useState<"en" | "zh" | "fr" | "es" | "de">("en");
  React.useEffect(() => {
    const handleLangChange = () => {
      const htmlLang = document.documentElement.lang?.toLowerCase().slice(0, 2);
      if (htmlLang === "zh") setLang("zh");else if (htmlLang === "fr") setLang("fr");else if (htmlLang === "es") setLang("es");else if (htmlLang === "de") setLang("de");else setLang("en");
    };
    handleLangChange();
    const observer = new MutationObserver(handleLangChange);
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["lang"]
    });
    return () => observer.disconnect();
  }, []);
  return lang;
};
const translations = {
  en: {
    philosophy: "Our Philosophy",
    heading: "Designed for the Moments That Matter",
    paragraph1: "We believe exceptional garments begin with deliberate restraint. Every piece in our collection is developed from architectural lines and enduring natural fibers, choosing pure mulberry silks, structured linens, and fine wools over ephemeral trends.",
    paragraph2: "Crafted for independent women navigating dynamic days and quiet evenings, our silhouettes offer refined ease and timeless versatility. It is clothing created to feel like a second skin, carrying quiet confidence into every room you enter.",
    learnMore: "Learn More About Our Craft"
  },
  zh: {
    philosophy: "我们的哲学",
    heading: "为重要时刻而设计",
    paragraph1: "我们相信卓越的服装始于刻意的克制。我们收藏中的每一件都是由建筑线条和耐久的天然纤维开发的，选择纯桑蚕丝、结构亚麻和精细羊毛而不是短暂的趋势。",
    paragraph2: "为独立女性设计，可以应对充满活力的日子和安静的夜晚，我们的剪裁提供精致的便利和永恒的多功能性。这是一种衣服，穿上它会像第二层皮肤一样感受，将安静的自信带入你进入的每个房间。",
    learnMore: "了解更多关于我们的工艺"
  },
  fr: {
    philosophy: "Notre Philosophie",
    heading: "Conçu pour les Moments qui Comptent",
    paragraph1: "Nous croyons que les vêtements exceptionnels commencent par une retenue délibérée. Chaque pièce de notre collection est développée à partir de lignes architecturales et de fibres naturelles durables, en choisissant les soies de mûrier pur, les lins structurés et les laines fines plutôt que les tendances éphémères.",
    paragraph2: "Créé pour les femmes indépendantes naviguant dans les jours dynamiques et les soirées tranquilles, nos silhouettes offrent une facilité raffinée et une polyvalence intemporelle. C'est un vêtement conçu pour se sentir comme une deuxième peau, apportant une confiance tranquille dans chaque pièce que vous entrez.",
    learnMore: "En Savoir Plus Sur Notre Métier"
  },
  es: {
    philosophy: "Nuestra Filosofía",
    heading: "Diseñado para los Momentos que Importan",
    paragraph1: "Creemos que las prendas excepcionales comienzan con una restricción deliberada. Cada pieza de nuestra colección se desarrolla a partir de líneas arquitectónicas y fibras naturales duraderas, eligiendo sedas de morera pura, linos estructurados y lanas finas sobre tendencias efímeras.",
    paragraph2: "Diseñado para mujeres independientes que navegan por días dinámicos y noches tranquilas, nuestras siluetas ofrecen facilidad refinada y versatilidad atemporal. Es ropa creada para sentirse como una segunda piel, llevando confianza tranquila a cada habitación en la que entras.",
    learnMore: "Aprende Más Sobre Nuestro Oficio"
  },
  de: {
    philosophy: "Unsere Philosophie",
    heading: "Für die Momente gestaltet, die zählen",
    paragraph1: "Wir glauben, dass außergewöhnliche Kleidung mit bewusster Zurückhaltung beginnt. Jedes Stück unserer Kollektion wird aus architektonischen Linien und langlebigen Naturfasern entwickelt, wobei wir reine Maulbeerseiden, strukturierte Leinen und feine Wolle gegenüber flüchtigen Trends wählen.",
    paragraph2: "Für unabhängige Frauen geschaffen, die dynamische Tage und ruhige Abende navigieren, bieten unsere Silhouetten raffinierten Komfort und zeitlose Vielseitigkeit. Es ist Kleidung, die sich wie eine zweite Haut anfühlt und stilles Vertrauen in jeden Raum trägt, den Sie betreten.",
    learnMore: "Erfahren Sie Mehr über Unser Handwerk"
  }
};
export default function Story() {
  const language = useWvcLanguage();
  const shouldReduceMotion = useReducedMotion();
  const fadeInVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 20
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.7,
        ease: [0.16, 1, 0.3, 1]
      }
    }
  };
  return <section id="品牌故事" data-nav="light" className="relative bg-background text-foreground py-16 sm:py-24 lg:py-32 border-b border-border transition-colors">
      <div className="max-w-[1360px] mx-auto px-6 sm:px-10 lg:px-16">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center">
          {/* Left Column: 5:7 Proportioned High-Detail Fabric/Tailoring Image */}
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: "-80px"
        }} variants={fadeInVariants} className="lg:col-span-6 xl:col-span-5">
            <div className="relative group overflow-hidden bg-muted border border-border">
              <AspectRatio ratio={5 / 7}>
                <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1794104/img/tailoring_fabric_detail.webp" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1794104/img/tailoring_fabric_detail.webp-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1794104/img/tailoring_fabric_detail.webp-wvc-sizes" alt="Fine tailoring and textured fabric drape in natural silk and wool" loading="lazy" decoding="async" className="w-full h-full object-cover object-center transition-transform duration-700 ease-out group-hover:scale-105" />
              </AspectRatio>
              <div className="absolute inset-0 ring-1 ring-inset ring-foreground/10 pointer-events-none" />
            </div>
          </motion.div>

          {/* Right Column: Narrative Editorial Content */}
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: "-80px"
        }} variants={fadeInVariants} className="lg:col-span-6 xl:col-span-7 flex flex-col justify-center lg:pl-6">
            {/* Hairline Divider & Eyebrow */}
            <div className="flex items-center gap-4 mb-6">
              <span className="h-px w-8 bg-border inline-block" />
              <p className="font-sans text-xs tracking-[0.18em] uppercase font-semibold text-muted-foreground">
                {translations[language].philosophy}
              </p>
            </div>

            {/* High-contrast Serif Headline */}
            <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-normal tracking-tight text-foreground leading-[1.15] mb-8">
              {translations[language].heading}
            </h2>

            {/* Narrative Body Paragraphs */}
            <div className="space-y-6 text-foreground/80 font-sans text-base sm:text-lg leading-relaxed max-w-xl font-light">
              <p>{translations[language].paragraph1}</p>
              <p>{translations[language].paragraph2}</p>
            </div>

            {/* Tertiary Editorial Action */}
            <div className="mt-10 pt-4 border-t border-border flex items-center">
              <Link to="/ShopPage" className="group inline-flex items-center gap-2 text-sm uppercase tracking-[0.14em] font-medium text-foreground hover:text-primary transition-colors py-2 relative">
                <span>{translations[language].learnMore}</span>
                <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" />
                <span className="absolute bottom-0 left-0 w-0 h-px bg-primary transition-all duration-300 group-hover:w-full" />
              </Link>
            </div>
          </motion.div>
        </div>
      </div>
    </section>;
}